import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Observable } from 'rxjs';
import { SeverityLevel } from '../../../framework/logging/severity-level.model';
import { LoggingService } from '../../../framework/logging/logging.service';
import { Message, MessageEnteredEvent, TypingEndEvent, User } from 'devextreme/ui/chat_types';
import { ITaskCommentPost, ITaskResponse, TaskCommentPost } from '../../../modules/tasks/model/types';
import { TaskDataService } from '../../services/task-data.service';
import { getNotificationTimeout } from '../../../types/resource';
import notify from 'devextreme/ui/notify';
import { ProfileDataService } from '../../services/profile-data.service';


@Component({
  standalone: false,
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.css']
})
export class ChatComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('chatComponent', { static: false }) chatInstance: any;
  @ViewChild('lookup', { static: false }) lookupInstance: any;

  @Input() task: ITaskResponse;


  @Output() taskCommentPostEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() unreadCommentCountChanged: EventEmitter<{ taskId: string, count: number }> = new EventEmitter<{ taskId: string, count: number }>();
  popupVisible: boolean = false;
  messagesSubject: BehaviorSubject<Message[]> = new BehaviorSubject([]);
  messages$: Observable<Message[]> = this.messagesSubject.asObservable();
  user: User
  responseUser: User
  textContent: any;
  typingUsers: User[] = [];
  caretPosition: number;
  messageToSend: ITaskCommentPost;
  mentions: string[] = [];
  responseMessage: Message;
  messages: Message[] = [];
  taskIdToUse: string;
  isIntialized: boolean = false;
  users: any[];
  filteredUsers: any[] = [];
  currentMentionQuery: string = '';
  replyToMessage: Message | null = null;
  selectedMentionIndex: number = -1;
  suppressNextMessageSubmit: boolean = false;
  lastMentionSelectionTime: number = Date.now() - 10000; // Initialize to 10 seconds ago to avoid false positive on first send
  mentionTextToRestore: string = '';

  private boundOnKeyDown: (e: KeyboardEvent) => void;
  private boundOnKeyUp: (e: Event) => void;
  private boundOnClick: () => void;
  private inputElement: HTMLTextAreaElement | null = null;

  constructor(public taskDataService: TaskDataService, public profileDataService: ProfileDataService, public loggingService: LoggingService, private cdr: ChangeDetectorRef) {
    this.onMessageEntered = this.onMessageEntered.bind(this);
    this.onContextMenuPreparing = this.onContextMenuPreparing.bind(this);
    this.loadUserData();


    this.user = { id: localStorage.getItem("loggedInUserId"), name: localStorage.getItem("loggedInUser") };
    this.responseUser = { id: '000000', name: 'system'};
    this.responseMessage = {author: this.responseUser};
    this.textContent= `Welcome to Task Manager chat`;

    this.messages$.subscribe(messages => {
      this.messages = messages;
    });
  }

    async loadUserData()
    {
    try{
          const us = await lastValueFrom(this.profileDataService.getAllUsers(false));
          this.users = us[1];
          this.filteredUsers = this.users; // Initialize filtered users
      }
      catch(err)
      {
          this.loggingService.logError(err, SeverityLevel.Error);
      }
    }

   filterUsers(query: string) {
     if (!query || query.length === 0) {
       this.filteredUsers = this.users;
     } else {
       this.filteredUsers = this.users.filter(user =>
         user.fullName?.toLowerCase().includes(query.toLowerCase()) ||
         user.email?.toLowerCase().includes(query.toLowerCase())
       );
     }

     if (this.filteredUsers.length === 0) {
       this.selectedMentionIndex = -1;
     } else if (this.selectedMentionIndex >= this.filteredUsers.length) {
       this.selectedMentionIndex = 0;
     }

    //  console.log('Filtered users for query:', query, 'Results:', this.filteredUsers.length);

     // Manually trigger change detection to update the dx-list
     this.cdr.detectChanges();
   }

   async getPreviousMessages(taskId: string)
   {
     try{
       const response = await lastValueFrom(this.taskDataService.getTaskComments(taskId));
       let comments: any[] = response[1];

       // Build server messages into an array
       const serverMessages: Message[] = [];
       comments.forEach((c)=>
      {
        let  m: Message = { id: c.taskId, author: {id: c.commenterId, name: `${c.commenterName}` }, text: c.comment, timestamp: c.updatedTimestamp};
        serverMessages.push(m);
      });

      // Prepend server messages: they should come first (history), followed by any optimistic messages sent during the load
      this.messages = [...serverMessages, ...this.messages];
      this.messagesSubject.next(this.messages);

      // After successfully loading comments, mark them as read
      await this.markCommentsAsRead(taskId);

     }
     catch(err: any)
     {
       this.loggingService.logError(err, SeverityLevel.Error);
       notify(err.message, 'error', getNotificationTimeout());
     }
   }

   async markCommentsAsRead(taskId: string): Promise<void> {
     try {
       await lastValueFrom(this.taskDataService.markAllCommentsAsReadForTask(taskId));

       // Immediately set unreadCommentCount to 0 on the task object
       if (this.task) {
         this.task.unreadCommentCount = 0;
         // Emit event to notify parent components
         this.unreadCommentCountChanged.emit({ taskId: this.task.id, count: 0 });
       }
     } catch (err: any) {
       this.loggingService.logError(err, SeverityLevel.Error);
       // Silently fail - don't show notification as this is a background operation
     }
   }

   onContextMenuPreparing(e: any) {
     const message = e.message;

     // Only show context menu for messages from other users
     if (message && message.author.id !== this.user.id) {
       e.items = [
         {
           text: 'Reply',
           icon: 'reply',
           onItemClick: () => this.replyToMessageHandler(message)
         }
       ];
     }
   }

   replyToMessageHandler(message: Message) {
     this.replyToMessage = message;

     // Find the user's email to add to mentions
     const userToReply = this.users.find(u =>
       u.fullName === message.author.name ||
       u.id === message.author.id
     );

     if (userToReply && !this.mentions.includes(userToReply.email)) {
       this.mentions.push(userToReply.email);
     }

     // Set focus to the input and add reply indicator text
     const inputElement = this.chatInstance.element.nativeElement.querySelector('textarea');
     if (inputElement) {
       inputElement.value = `Replying to ${message.author.name}: `;
       inputElement.focus();
       this.caretPosition = inputElement.value.length;
       inputElement.setSelectionRange(this.caretPosition, this.caretPosition);
     }
   }

   cancelReply() {
     this.replyToMessage = null;
     const inputElement = this.chatInstance.element.nativeElement.querySelector('textarea');
     if (inputElement) {
       inputElement.value = '';
       this.caretPosition = 0;
     }
   }





   onUserSelected(e: any) {
    const selectedUser = e.itemData; // dx-list uses itemData instead of value
    this.selectMentionedUser(selectedUser);
   }

   selectMentionedUser(selectedUser: any) {
    if (!selectedUser) {
      return;
    }

    // console.log('User selected for mention:', selectedUser.fullName);
    this.insertUser(selectedUser.fullName);

    // Store the textarea content (with inserted mention) to restore it if submission is suppressed
    const inputElement = this.chatInstance.element.nativeElement.querySelector('textarea');
    if (inputElement) {
      this.mentionTextToRestore = inputElement.value;
    }

    // Add to mentions array for notification purposes
    if (!this.mentions.includes(selectedUser.email)) {
      this.mentions.push(selectedUser.email);
      // console.log('Added to mentions:', selectedUser.email);
    }

    this.lastMentionSelectionTime = Date.now();
    this.popupVisible = false;
    this.selectedMentionIndex = -1;
    this.currentMentionQuery = '';
    // Don't set suppressNextMessageSubmit here - only set it in onKeyDown when Enter is pressed
   }

   isMentionSelected(user: any): boolean {
    return this.selectedMentionIndex > -1 && this.filteredUsers[this.selectedMentionIndex] === user;
   }

   onKeyDown(event: KeyboardEvent) {
    if (!this.popupVisible || this.filteredUsers.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      this.moveMentionSelection(1);
      (event.target as HTMLTextAreaElement)?.focus();
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      this.moveMentionSelection(-1);
      (event.target as HTMLTextAreaElement)?.focus();
      return;
    }

    if (event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();

      if (this.selectedMentionIndex === -1) {
        this.moveMentionSelection(1);
        (event.target as HTMLTextAreaElement)?.focus();
      } else {
        this.selectMentionedUser(this.filteredUsers[this.selectedMentionIndex]);
      }

      return;
    }

    if (event.key === 'Enter' && this.selectedMentionIndex > -1) {
      event.preventDefault();
      event.stopPropagation();
      this.suppressNextMessageSubmit = true;
      this.selectMentionedUser(this.filteredUsers[this.selectedMentionIndex]);
      return;
    }
   }

   private moveMentionSelection(direction: number) {
    if (this.filteredUsers.length === 0) {
      this.selectedMentionIndex = -1;
      return;
    }

    if (this.selectedMentionIndex === -1) {
      this.selectedMentionIndex = direction > 0 ? 0 : this.filteredUsers.length - 1;
    } else {
      this.selectedMentionIndex = (this.selectedMentionIndex + direction + this.filteredUsers.length) % this.filteredUsers.length;
    }

    this.cdr.detectChanges();
    this.scrollSelectedMentionIntoView();
   }

   private scrollSelectedMentionIntoView() {
    setTimeout(() => {
      const popupElement = document.getElementById('userLookup');
      const selectedItem = popupElement?.querySelector('.dx-list-item.keyboard-selected');
      selectedItem?.scrollIntoView({ block: 'nearest' });
    }, 0);
   }

   insertUser(userName: string) {
    const inputElement = this.chatInstance.element.nativeElement.querySelector('textarea');
    const text = inputElement.value;
    const caretPosition = this.caretPosition;

    // Find the last @ symbol before the caret position
    const textBeforeCaret = text.substring(0, caretPosition);
    const lastAtIndex = textBeforeCaret.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      // Replace text from @ to caret position with the username
      const beforeAt = text.substring(0, lastAtIndex);
      const afterCaret = text.substring(caretPosition);
      const newText = `${beforeAt}@${userName} ${afterCaret}`;

      inputElement.value = newText;

      // Set cursor position after the inserted username
      const newCaretPosition = lastAtIndex + userName.length + 2; // +2 for @ and space
      inputElement.setSelectionRange(newCaretPosition, newCaretPosition);
      this.caretPosition = newCaretPosition;

      // console.log('User mention inserted:', userName);
    } else {
      // Fallback to original behavior
      const beforeCaret = text.slice(0, caretPosition);
      const afterCaret = text.slice(caretPosition);
      inputElement.value = `${beforeCaret}@${userName} ${afterCaret}`;
    }

    // Focus back to the input
    inputElement.focus();
  }

   onTypingStart(e: any)
   {
    this.typingUsers.push(this.user);
   }

   onTypingEnd(e: TypingEndEvent)
   {
    this.typingUsers = [];
   }

   async onMessageEntered(e: MessageEnteredEvent) {
    // If a mention was just selected within the last 200ms, suppress the message submission
    const timeSinceLastMention = Date.now() - this.lastMentionSelectionTime;
    if (this.suppressNextMessageSubmit || timeSinceLastMention < 200) {
      // Reset the flag so subsequent sends are not permanently blocked
      this.suppressNextMessageSubmit = false;
      // Restore the textarea content with the inserted mention after the chat clears it
      if (this.mentionTextToRestore) {
        setTimeout(() => {
          const inputElement = this.chatInstance.element.nativeElement.querySelector('textarea');
          if (inputElement) {
            inputElement.value = this.mentionTextToRestore;
            // Fire a native input event so DevExtreme updates its internal state and removes the placeholder
            inputElement.dispatchEvent(new Event('input', { bubbles: true }));
            inputElement.focus();
            // Set caret position to the end of the inserted mention
            inputElement.setSelectionRange(inputElement.value.length, inputElement.value.length);
            this.cdr.detectChanges();
          }
        }, 0);
      }
      return;
    }

    try{
          // Guard: taskIdToUse should be set by setTaskIdAndGetPreviousMessages(), but
          // derive it here as a fallback in case the accordion onSelectionChanged did not
          // fire before the user submitted (timing edge-case).
          if (!this.taskIdToUse && this.task) {
            this.taskIdToUse = this.task.parentId ? this.task.parentId : this.task.id;
          }

          this.messageToSend = new TaskCommentPost()
          this.messages = [...this.messages, e.message];
          this.messagesSubject.next(this.messages);

          this.messageToSend.comment =  e.message.text;
          this.messageToSend.commenterId = this.user.id;
          this.messageToSend.headline = this.taskIdToUse
          this.messageToSend.mentions = this.mentions;
          this.messageToSend.taskId = this.taskIdToUse;

          const response = await lastValueFrom(this.taskDataService.postComment(this.messageToSend));

          if(response[1] == "Comment inserted successfully")
          {
            this.responseMessage.text = "Notification sent";
            this.responseMessage.timestamp = new Date();
            // Immediately reflect the new comment in the task object so the
            // grid button icon turns red without waiting for a SignalR round-trip.
            if (this.task) {
              this.task.commentCount = (this.task.commentCount || 0) + 1;
            }
            this.taskCommentPostEmitter.emit(this.taskIdToUse);
          }
          else{

            this.responseMessage.text = "Notification failed to send";
            this.responseMessage.timestamp = new Date();
          }

          this.messages = [...this.messages, this.responseMessage];
          this.messagesSubject.next(this.messages);



    }
    catch(err: any)
    {
      this.loggingService.logError(err, SeverityLevel.Error);
      notify(err.message, 'error', getNotificationTimeout());
    }

    this.mentions.length = 0;
    this.replyToMessage = null;

  }

  onKeyUp(event: any) {
    if (this.isMentionNavigationKey(event.key)) {
      return;
    }

    const text = event.target.value;
    const caretPosition = event.target.selectionStart;
    this.caretPosition = caretPosition;

    // Find the last @ symbol before the caret position
    const textBeforeCaret = text.substring(0, caretPosition);
    const lastAtIndex = textBeforeCaret.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      // Check if there's a space between @ and caret (which would close the mention)
      const textAfterAt = textBeforeCaret.substring(lastAtIndex + 1);
      const hasSpaceAfterAt = textAfterAt.includes(' ') || textAfterAt.includes('\n');

      if (!hasSpaceAfterAt) {
        // We're in the middle of typing a mention
        this.currentMentionQuery = textAfterAt;
        this.filterUsers(this.currentMentionQuery);
        this.popupVisible = true;

        if (!this.currentMentionQuery) {
          this.selectedMentionIndex = -1;
        }
        // console.log('Mentions popup activated - typing:', textAfterAt);

        // Keep focus on the input element
        setTimeout(() => {
          event.target.focus();
        }, 0);
      } else {
        // There's a space after @, so close the popup
        this.popupVisible = false;
        this.currentMentionQuery = '';
        this.selectedMentionIndex = -1;
      }
    } else {
      // No @ found, close the popup
      this.popupVisible = false;
      this.currentMentionQuery = '';
      this.selectedMentionIndex = -1;
    }

    // Also close popup on Escape key
    if (event.key === 'Escape') {
      this.popupVisible = false;
      this.currentMentionQuery = '';
      this.selectedMentionIndex = -1;

      // If Escape is pressed and we're in reply mode, cancel the reply
      if (this.replyToMessage) {
        this.cancelReply();
      }
    }
  }

  private isMentionNavigationKey(key: string): boolean {
    return key === 'ArrowDown' || key === 'ArrowUp' || key === 'Tab' || key === 'Enter';
  }

  ngAfterViewInit(): void {
    // DevExtreme renders dx-chat's internal elements asynchronously.
    // Wait 100ms to ensure the textarea exists before attaching listeners.
    setTimeout(() => {
      const chatEl = this.chatInstance?.element?.nativeElement;
      this.inputElement = chatEl?.querySelector('textarea') as HTMLTextAreaElement;

      if (this.inputElement) {
        this.boundOnKeyDown = this.onKeyDown.bind(this);
        this.boundOnKeyUp = this.onKeyUp.bind(this);
        this.boundOnClick = () => { this.caretPosition = this.inputElement!.selectionStart; };

        this.inputElement.addEventListener('keydown', this.boundOnKeyDown);
        this.inputElement.addEventListener('keyup', this.boundOnKeyUp);
        this.inputElement.addEventListener('click', this.boundOnClick);

        // Clicking DevExtreme wrapper divs inside the messagebox (e.g. padding
        // areas) transfers focus away from the textarea without giving it back.
        // Intercept those mousedowns and keep focus on the native textarea.
        const messagebox = chatEl?.querySelector('.dx-chat-messagebox');
        if (messagebox) {
          messagebox.addEventListener('mousedown', (e: MouseEvent) => {
            const isInsideTextarea = e.target === this.inputElement || this.inputElement?.contains(e.target as Node);
            if (!isInsideTextarea) {
              e.preventDefault();
              this.inputElement?.focus();
            }
          });
        }

        // Dispatch a bubbling focusin so DevExtreme detects focus on its wrapper
        // element and adds dx-state-focused / hides the placeholder correctly.
        const focusFn = () => {
          this.inputElement?.focus();
          this.inputElement?.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
        };
        setTimeout(focusFn, 100);
      }

      this.enableMouseWheelScrolling();
    }, 100);
  }
  private enableMouseWheelScrolling(): void {
    setTimeout(() => {
      const chatElement = this.chatInstance?.element?.nativeElement;
      if (!chatElement) {
        console.warn('Chat element not found');
        return;
      }

      const messageList = chatElement.querySelector('.dx-chat-messagelist');

      if (!messageList) {
        console.warn('Message list not found');
        return;
      }

      // Ensure the message list is scrollable with native scrolling
      // The CSS handles the layout, this just ensures overflow is set
      (messageList as HTMLElement).style.overflowY = 'auto';
      (messageList as HTMLElement).style.overflowX = 'hidden';

      // console.log('Chat messagelist scrolling enabled');
    }, 500);
  }

  setTaskIdAndGetPreviousMessages(){
     this.taskIdToUse = this.task?.parentId ? this.task.parentId : this.task.id;
     this.messages = [];
     this.messagesSubject.next(this.messages);
     this.getPreviousMessages(this.taskIdToUse);
  }

  ngOnInit() {
    // Only mark as initialized, don't automatically load messages
    // Let the parent component control when to load messages
    this.isIntialized = true;
  }
  ngOnDestroy(): void {
    this.isIntialized = false;
    if (this.inputElement) {
      this.inputElement.removeEventListener('keydown', this.boundOnKeyDown);
      this.inputElement.removeEventListener('keyup', this.boundOnKeyUp);
      this.inputElement.removeEventListener('click', this.boundOnClick);
      this.inputElement = null;
    }
  }

}
